// IMG-8: 8-DOF projective mapping from 4 point pairs. DLT → 8×8 linear system solved by
// Gaussian elimination with partial pivoting. Matrices are row-major 3×3 (number[9]).
import { angleOf, normalizeDeg, sub, type Vec } from "./geometry";

/** Solve H such that H·src[i] ≈ dst[i] (homogeneous). Null when degenerate (IMG-8). */
export function solveHomography(src: Vec[], dst: Vec[]): number[] | null {
  if (src.length !== 4 || dst.length !== 4) return null;
  // Build the 8×9 system A·h = b with h = [h0..h7], h8 = 1.
  const A: number[][] = [];
  const b: number[] = [];
  for (let i = 0; i < 4; i++) {
    const { x, y } = src[i]!;
    const { x: u, y: v } = dst[i]!;
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
    b.push(u);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
    b.push(v);
  }
  const h = gaussSolve(A, b);
  if (!h) return null;
  return [...h, 1];
}

/** Gaussian elimination with partial pivoting; null when the system is singular. */
function gaussSolve(A: number[][], b: number[]): number[] | null {
  const n = b.length;
  const M = A.map((row, i) => [...row, b[i]!]);
  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(M[row]![col]!) > Math.abs(M[pivot]![col]!)) pivot = row;
    }
    if (Math.abs(M[pivot]![col]!) < 1e-10) return null; // IMG-8 condition check
    [M[col], M[pivot]] = [M[pivot]!, M[col]!];
    for (let row = col + 1; row < n; row++) {
      const f = M[row]![col]! / M[col]![col]!;
      for (let k = col; k <= n; k++) M[row]![k]! -= f * M[col]![k]!;
    }
  }
  const x = new Array<number>(n).fill(0);
  for (let row = n - 1; row >= 0; row--) {
    let sum = M[row]![n]!;
    for (let k = row + 1; k < n; k++) sum -= M[row]![k]! * x[k]!;
    x[row] = sum / M[row]![row]!;
  }
  return x;
}

export function applyHomography(h: number[], p: Vec): Vec {
  const w = h[6]! * p.x + h[7]! * p.y + h[8]!;
  return {
    x: (h[0]! * p.x + h[1]! * p.y + h[2]!) / w,
    y: (h[3]! * p.x + h[4]! * p.y + h[5]!) / w
  };
}

/** Inverse of a 3×3 row-major matrix; null when singular. */
export function invertHomography(h: number[]): number[] | null {
  const [a, b, c, d, e, f, g, i, j] = h as [number, ...number[]] & number[];
  const A = e! * j! - f! * i!;
  const B = -(d! * j! - f! * g!);
  const C = d! * i! - e! * g!;
  const det = a! * A + b! * B + c! * C;
  if (Math.abs(det) < 1e-12) return null;
  return [
    A / det,
    -(b! * j! - c! * i!) / det,
    (b! * f! - c! * e!) / det,
    B / det,
    (a! * j! - c! * g!) / det,
    -(a! * f! - c! * d!) / det,
    C / det,
    -(a! * i! - b! * g!) / det,
    (a! * e! - b! * d!) / det
  ];
}

export interface QuadValidation {
  ok: boolean;
  reason?: string;
}

/**
 * IMG-8 pin validation: strictly convex, interior angles in (10°, 170°),
 * area ≥ minArea. Points must be in consistent winding order.
 */
export function validateQuad(pts: Vec[], minArea: number): QuadValidation {
  if (pts.length !== 4) return { ok: false, reason: "Four corners required" };
  // convexity: cross-product signs consistent
  let sign = 0;
  for (let i = 0; i < 4; i++) {
    const a = pts[i]!;
    const b = pts[(i + 1) % 4]!;
    const c = pts[(i + 2) % 4]!;
    const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    const s = Math.sign(cross);
    if (s === 0) return { ok: false, reason: "Corners must not be in a straight line" };
    if (sign === 0) sign = s;
    else if (s !== sign) return { ok: false, reason: "Corners must form a convex rectangle-ish shape" };
  }
  // interior angles in (10°, 170°)
  for (let i = 0; i < 4; i++) {
    const prev = pts[(i + 3) % 4]!;
    const cur = pts[i]!;
    const next = pts[(i + 1) % 4]!;
    let ang = normalizeDeg(angleOf(sub(next, cur)) - angleOf(sub(prev, cur)));
    if (ang > 180) ang = 360 - ang;
    if (ang <= 10 || ang >= 170) return { ok: false, reason: "Corner angles are too extreme" };
  }
  if (Math.abs(polygonArea(pts)) < minArea) return { ok: false, reason: "The marked area is too small" };
  return { ok: true };
}

export function polygonArea(pts: Vec[]): number {
  let area = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i]!;
    const b = pts[(i + 1) % pts.length]!;
    area += a.x * b.y - b.x * a.y;
  }
  return area / 2;
}

/** IMG-7 pin auto-ordering: TL,TR,BR,BL by angle around the centroid. */
export function orderQuad(pts: Vec[]): Vec[] {
  const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
  const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
  const sorted = [...pts].sort(
    (a, b) => Math.atan2(a.y - cy, a.x - cx) - Math.atan2(b.y - cy, b.x - cx)
  );
  // rotate so the first point is the one most top-left
  let best = 0;
  for (let i = 1; i < 4; i++) {
    if (sorted[i]!.x + sorted[i]!.y < sorted[best]!.x + sorted[best]!.y) best = i;
  }
  return [sorted[best]!, sorted[(best + 1) % 4]!, sorted[(best + 2) % 4]!, sorted[(best + 3) % 4]!];
}

/** IMG-7 auto-rectangle target: width = avg of top/bottom edges, height = avg of sides. */
export function autoRectSize(quad: Vec[]): { w: number; h: number } {
  const d = (a: Vec, b: Vec) => Math.hypot(a.x - b.x, a.y - b.y);
  return {
    w: (d(quad[0]!, quad[1]!) + d(quad[3]!, quad[2]!)) / 2,
    h: (d(quad[0]!, quad[3]!) + d(quad[1]!, quad[2]!)) / 2
  };
}
